import React, { useEffect, useState } from "react";
import "../styles/login.css";
import "../styles/provider-login.css";
import { useForm } from "react-hook-form";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import Cookies from "js-cookie";
import { Link, useNavigate } from "react-router-dom";
import { API_DETAILS, COOKIE_KEYS } from "../App";
import { useChatContext } from "../context/ChatContext";
import "../styles/Home.css";

const Login = () => {
  const navigate = useNavigate();
  const [forgot, setForgot] = useState(0);
  const { setIsLoggedIn } = useChatContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    // remove existing cookies before login (as you already do)
    Cookies.remove(COOKIE_KEYS.userId);
    Cookies.remove(COOKIE_KEYS.token);
    Cookies.remove(COOKIE_KEYS.userType);
    Cookies.remove(COOKIE_KEYS.userName);
  }, []);

  const onSubmit = (formData) => {
    // IMPORTANT: do NOT send "role" to backend (to avoid breaking your API)
    const payload = {
      username: formData.username,
      password: formData.password,
    };

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    axios
      .post(
        API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          "/auth/login",
        payload,
        config
      )
      .then((res) => {
        setIsLoggedIn(true);

        if (res.data.uType === "ADMIN") {
          toast.success("Successful login");
          Cookies.set(COOKIE_KEYS.token, res.data.token, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userId, res.data.userId, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userType, res.data.utype, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userName, res.data.fullName, { expires: 7 });
          navigate("/admin-doctors");
          return;
        }

        if (res.data.verificationStatus === true) {
          toast.success("Successful login");
          Cookies.set(COOKIE_KEYS.token, res.data.token, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userId, res.data.userId, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userType, res.data.utype, { expires: 7 });
          Cookies.set(COOKIE_KEYS.userName, res.data.fullName, { expires: 7 });

          if (res.data.utype === "PATIENT") {
            navigate("/patient-home");
          } else if (res.data.utype === "PROVIDER") {
            navigate("/doctor-home");
          } else {
            navigate("/admin-doctors");
          }
        } else {
          navigate("/verify-email", { state: { userEmail: res.data.email } });
        }
      })
      .catch((err) => {
        console.error(err?.response?.data?.errorMessage || err);
        toast.error("Oops!! Please check your username and password");
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  // If user clicked forgot password
  useEffect(() => {
    if (forgot === 1) navigate("/forgot-password");
  }, [forgot, navigate]);

  return (
    <div className="login-page" onKeyDown={handleKeyDown}>
      <ToastContainer />

      {/* Top nav */}
      <header className="home-nav">
          <Link to="/" className="home-nav-left" aria-label="Youro home">
            <div className="home-nav-logo-circle">Y</div>
            <span className="home-nav-logo-text">Youro</span>
          </Link>
          <nav className="home-nav-center">
            <button
              className="home-nav-link"
              onClick={() => navigate("/about")}
            >
              About
            </button>
            <button
              className="home-nav-link"
              onClick={() => navigate("/conditions")}
            >
              Find Your Diagnosis
            </button>
            <button
              className="home-nav-link"
              onClick={() => navigate("/assessment")}
            >
              Symptom Assessment
            </button>
          </nav>
          <div className="home-nav-right">
            <button
              className="home-nav-signin"
              onClick={() => navigate("/login")}
            >
              Sign In
            </button>
            <button
              className="home-nav-get-started"
              onClick={() => navigate("/signup")}
            >
              Get Started
            </button>
          </div>
      </header>

      <main className="login-main">
        <button className="login-back" type="button" onClick={() => navigate("/")}>
          <span className="login-back-icon">←</span>
          Back to Home
        </button>

        <section className="login-card">
          <h1 className="login-title">Welcome back</h1>
          <p className="login-subtitle">Sign in to access your health dashboard</p>

          {/* Email */}
          <label className="login-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className={"login-input" + (errors.username ? " login-input-error" : "")}
            type="text"
            placeholder="john@example.com"
            {...register("username", {
              required: "This field is required",
              maxLength: { value: 64, message: "Email cannot exceed 64 characters" },
              pattern: {
                value:
                  /([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+/,
                message: "Please enter valid email",
              },
            })}
          />
          {errors.username && <p className="login-error">{errors.username.message}</p>}

          {/* Password */}
          <label className="login-label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className={"login-input" + (errors.password ? " login-input-error" : "")}
            type="password"
            placeholder="••••••••"
            {...register("password", {
              required: "This field is required",
              maxLength: { value: 32, message: "Password cannot exceed 32 characters" },
              minLength: { value: 8, message: "Password is min 8 characters" },
            })}
          />
          {errors.password && <p className="login-error">{errors.password.message}</p>}

          <div className="login-forgot-row">
            <button
              type="button"
              className="login-forgot"
              onClick={() => setForgot(1)}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="button"
            className="login-submit"
            onClick={handleSubmit(onSubmit)}
          >
            Sign In <span className="login-submit-arrow">→</span>
          </button>

          <p className="login-bottom-text">
            Don't have an account?{" "}
            <button
              type="button"
              className="login-signup-link"
              onClick={() => navigate("/signup")}
            >
              Sign up
            </button>
          </p>

          <div className="provider-login-divider" />

          <p className="provider-login-alt">
            Are you a healthcare provider?{" "}
            <button
              type="button"
              className="provider-login-patient-link"
              onClick={() => navigate("/provider-login")}
            >
              Provider sign in
            </button>
          </p>
        </section>
      </main>
    </div>
  );
};

export default Login;
